import { ref, Ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import { IPRuleItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { IPRestrictionViewData } from './IPRestrictionListHandler';
type Handler = {
  itemInfo: Ref<SMH.IPRestrictionItem | {}>;
  ipPattern: RegExp;
  checkIPAlreadyContained: (ipString: string, listData: IPRestrictionViewData[], itemID?: number) => Promise<boolean>;
  createRestriction: (ipString: string) => Promise<boolean>;
  modifyRestriction: (itemID: number, ipString: string) => Promise<boolean>;
  editRestrictionState: (itemID: number, state: boolean) => Promise<boolean>;
  deleteRestriction: (itemID: number) => Promise<boolean>;
};
export default function IPRestrictionSubmitHandler(): Handler {
  const itemInfo: Ref<SMH.IPRestrictionItem | {}> = ref({});
  const ipPattern = /^(((25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(25[0-5]|2[0-4]\d|1?\d?\d)[-$]?)+$/;
  const matchPatten = /((2(5[0-5]|[0-4]\d))|[0-1]?\d{1,2})(\.((2(5[0-5]|[0-4]\d))|[0-1]?\d{1,2})){3}/g;
  const methods = {
    // Public Methods
    /**
     * 检查新的ip地址是否和现有的ip重复
     * @param ipString 输入的ip字符串
     * @param listData 已有规则的数据
     * @param itemID 修改时，对应规则的id，如果传入则listData中该id的规则不会用于比对，可选参数
     * @returns 如果现有规则已包含新的规则，将返回true，否则返回false
     */
    async checkIPAlreadyContained(ipString: string, listData: IPRestrictionViewData[], itemID?: number) {
      if (listData.length === 0) {
        return false;
      }
      const ruleList = privateMethods._parse(ipString);
      if (ruleList.length === 0) {
        return false;
      }
      const ipData: IPRuleItem[] = listData
        .filter((item: IPRestrictionViewData) => {
          if (itemID) {
            if (item.item.id === itemID) {
              return false;
            }
          }
          return true;
        })
        .map((item: IPRestrictionViewData) => {
          if (item.item.rule.endIp) {
            return {
              startIp: item.item.rule.startIp,
              endIp: item.item.rule.endIp,
            };
          }
          return {
            startIp: item.item.rule.startIp,
          };
        });
      try {
        ipData.forEach((existRule: IPRuleItem) => {
          ruleList.forEach((newRule: IPRuleItem) => {
            if (privateMethods._isOverlap(newRule, existRule)) {
              tips.error(
                i18n_t('新规则：{slot1}已包含在规则：{slot2}中', {
                  slot1: privateMethods._ruleToString(newRule),
                  slot2: privateMethods._ruleToString(existRule),
                }),
              );
              throw {};
            }
          });
        });
        return false;
      } catch {
        return true;
      }
    },
    async createRestriction(ipString: string) {
      let succeed = true;
      try {
        tips.loading(i18n_t('正在处理'), 0);
        const ruleList = privateMethods._parse(ipString);
        if (ruleList.length === 0) {
          tips.error(i18n_t('IP格式错误，请检查后重试'));
          return false;
        }
        await smh.userAPI.addIPRestriction(null, {
          body: ruleList,
        });
        tips.success(i18n_t('创建成功'), 2000);
      } catch (error: any) {
        if (error.code === 'InvalidIpRange' && error.extraInfo && error.extraInfo.startIp) {
          tips.error(
            i18n_t('IP规则{slot1}在已有配置IP中，请删除后再保存', {
              slot1: privateMethods._ruleToString({
                startIp: error.extraInfo.startIp,
                endIp: error.extraInfo.endIp,
              }),
            }),
          );
        } else {
          tips.error(error as MsgError);
        }
        succeed = false;
      }
      return succeed;
    },
    async modifyRestriction(itemID: number, ipString: string) {
      const params: SMH.EditIPRestrictionParams = {
        id: itemID,
        startIp: '',
      };
      const pattern = RegExp(ipPattern, 'g');
      const config = ipString.match(matchPatten);
      if (!config) {
        tips.error(i18n_t('IP格式错误，请检查后重试'));
        return false;
      }
      if (config.length === 1) {
        [params.startIp] = config;
      } else if (config.length === 2) {
        [params.startIp, params.endIp] = config;
      } else {
        tips.error(i18n_t('IP格式错误，请检查后重试'));
        return false;
      }
      let succeed = true;
      try {
        tips.loading(i18n_t('正在处理'), 0);
        await smh.userAPI.editIPRestriction(params);
        tips.success(i18n_t('修改成功'));
      } catch (error: any) {
        succeed = false;
        if (error.code === 'InvalidIpRange' && error.extraInfo && error.extraInfo.startIp) {
          tips.error(
            i18n_t('IP规则{slot1}在已有配置IP中，请删除后再保存', {
              slot1: privateMethods._ruleToString({
                startIp: error.extraInfo.startIp,
                endIp: error.extraInfo.endIp,
              }),
            }),
          );
        } else {
          tips.error(error as MsgError);
        }
      }
      return succeed;
    },
    /**
     *
     * @param itemID ip id
     * @param state ip状态
     * @returns 返回成功与失败
     */
    async editRestrictionState(itemID: number, state: boolean) {
      let succeed = true;
      try {
        tips.loading(i18n_t('正在处理'), 0);
        const params: SMH.EditIPRestrictionParams = {
          id: itemID,
          enabled: state,
        };
        await smh.userAPI.editIPRestriction(params);
        tips.success(state ? i18n_t('已恢复当前规则') : i18n_t('已暂停当前规则'));
      } catch (e) {
        succeed = false;
        tips.error(e as MsgError);
      }
      return succeed;
    },
    async deleteRestriction(itemID: number) {
      let succeed = true;
      try {
        tips.loading(i18n_t('正在处理'), 0);
        await smh.userAPI.deleteIPRestriction({
          id: itemID,
        });
        tips.success(i18n_t('已删除当前规则'));
      } catch (e) {
        succeed = false;
        tips.error(e as MsgError);
      }
      return succeed;
    },
  };
  const privateMethods = {
    // Private Methods
    _parse(ipString: string) {
      const ipList = ipString.split(';').filter((item: string) => {
        return item.length > 0;
      });
      const ruleList: Array<IPRuleItem> = [];
      ipList.forEach((item: string) => {
        const config = item.match(matchPatten);
        if (config?.length === 1) {
          ruleList.push({
            startIp: config[0],
          });
        } else if (config?.length === 2) {
          ruleList.push({
            startIp: config[0],
            endIp: config[1],
          });
        }
      });
      return ruleList;
    },
    _ipToNum(ip: string) {
      const ipArray = ip.split('.');
      let result = 0;
      for (let i = 0; i < ipArray.length; i++) {
        const power = 3 - i;
        const value = Number(ipArray[i]);
        result += value * 256 ** power;
      }
      return result;
    },
    _compare(ip1: string, ip2: string) {
      const ip1Number = privateMethods._ipToNum(ip1);
      const ip2Number = privateMethods._ipToNum(ip2);
      if (ip1Number === ip2Number) {
        return 0;
      }
      return ip1Number > ip2Number ? 1 : -1;
    },
    _isInclude(ip: string, inIPRegion: IPRuleItem) {
      if (inIPRegion.endIp) {
        return (
          privateMethods._compare(ip, inIPRegion.startIp) >= 0 && privateMethods._compare(ip, inIPRegion.endIp) <= 0
        );
      }
      return privateMethods._compare(ip, inIPRegion.startIp) === 0;
    },
    _isOverlap(ipRegion1: IPRuleItem, ipRegion2: IPRuleItem) {
      if (ipRegion1.endIp) {
        return (
          privateMethods._isInclude(ipRegion1.startIp, ipRegion2) ||
          privateMethods._isInclude(ipRegion1.endIp, ipRegion2)
        );
      }
      return privateMethods._isInclude(ipRegion1.startIp, ipRegion2);
    },
    _ruleToString(rule: IPRuleItem) {
      let string = rule.startIp;
      if (rule.endIp) {
        string = string.concat('-').concat(rule.endIp);
      }
      return string;
    },
  };
  return {
    itemInfo,
    ipPattern,
    ...methods,
  };
}
